/**
 * 🔄 PAYMENT SYNC TAB
 * Allows admins to manually sync stuck payments from Paystack
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { Sync, CheckCircle, Error as ErrorIcon, Info } from '@mui/icons-material';
import { getApiBaseUrl } from '@/config/environment';

interface SyncResult {
  success: boolean;
  message: string;
  data?: {
    reference: string;
    amount: number;
    status: string;
    channel: string;
    paidAt?: string;
    bookingUpdated?: boolean;
    alreadyProcessed?: boolean;
  };
}

export function PaymentSyncTab() {
  const [paymentReference, setPaymentReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSync = async () => {
    if (!paymentReference.trim()) {
      setError('Please enter a payment reference');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/payments/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reference: paymentReference.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle error response per API contract
        const errorMessage = data.error?.message || data.message || 'Failed to sync payment';
        const errorCode = data.error?.code || 'UNKNOWN_ERROR';
        throw new Error(`${errorCode}: ${errorMessage}`);
      }

      setResult(data);
      setPaymentReference(''); // Clear input on success
    } catch (err: any) {
      console.error('Payment sync error:', err);
      setError(err.message || 'Failed to sync payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSync();
    }
  };

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Sync color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Manual Payment Sync
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Manually sync payment status from Paystack when a payment is stuck or webhook failed.
            Enter the payment reference to verify and update the payment status.
          </Typography>
        </CardContent>
      </Card>

      {/* Sync Form */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Payment Reference
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                placeholder="e.g., POLAR-1773956383529-KCD255"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                error={!!error && !paymentReference.trim()}
                helperText={error && !paymentReference.trim() ? error : ''}
                sx={{ maxWidth: 500 }}
              />
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Sync />}
                onClick={handleSync}
                disabled={loading || !paymentReference.trim()}
                sx={{ minWidth: 120 }}
              >
                {loading ? 'Syncing...' : 'Sync'}
              </Button>
            </Box>
          </Box>

          {/* Error Alert */}
          {error && paymentReference.trim() && (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600}>
                Sync Failed
              </Typography>
              <Typography variant="caption">{error}</Typography>
            </Alert>
          )}

          {/* Success Result */}
          {result && result.success && (
            <Alert
              severity={result.data?.alreadyProcessed ? 'info' : 'success'}
              icon={<CheckCircle />}
              sx={{ mb: 3 }}
            >
              <Typography variant="body2" fontWeight={600}>
                {result.message}
              </Typography>
              {result.data?.alreadyProcessed && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  This payment has already been processed and synced.
                </Typography>
              )}
            </Alert>
          )}

          {/* Payment Details */}
          {result && result.data && !result.data.alreadyProcessed && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Payment Details
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Reference"
                    secondary={result.data.reference}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Amount"
                    secondary={`₦${result.data.amount.toLocaleString()}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Status"
                    secondary={
                      <Chip
                        label={result.data.status}
                        color={result.data.status === 'completed' ? 'success' : 'default'}
                        size="small"
                      />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Payment Channel"
                    secondary={result.data.channel}
                  />
                </ListItem>
                {result.data.paidAt && (
                  <ListItem>
                    <ListItemText
                      primary="Paid At"
                      secondary={new Date(result.data.paidAt).toLocaleString()}
                    />
                  </ListItem>
                )}
                {result.data.bookingUpdated !== undefined && (
                  <ListItem>
                    <ListItemText
                      primary="Booking Updated"
                      secondary={
                        <Chip
                          label={result.data.bookingUpdated ? 'Yes - Confirmed' : 'No'}
                          color={result.data.bookingUpdated ? 'success' : 'default'}
                          size="small"
                        />
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {/* Info Box */}
          <Alert severity="info" icon={<Info />} sx={{ mt: 3 }}>
            <Typography variant="caption" display="block" gutterBottom>
              <strong>How it works:</strong>
            </Typography>
            <Typography variant="caption" component="div">
              1. Enter the payment reference from Paystack
            </Typography>
            <Typography variant="caption" component="div">
              2. System verifies payment status with Paystack API
            </Typography>
            <Typography variant="caption" component="div">
              3. Updates payment record in database
            </Typography>
            <Typography variant="caption" component="div">
              4. If successful, confirms the associated booking
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
}
